import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, NgForm, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { OperateursService } from '../../../../../../../data/intervenant/services/operateurs/operateurs.service';
import { Pays } from '../../../../../../../data/intervenant/models/pays.model';
import { Operateur } from '../../../../../../../data/intervenant/models/operateur.model';
import {InformationMajeure} from "../../../../../../../data/intervenant/models/information-majeure.model";
import {SessionContext} from "../../../../../../../core/services/config/app.settings";
import {CommentaireModificationComponent} from "../commentaire-modification/commentaire-modification.component";
import {ModificationMineure} from "../../../../../../../data/intervenant/models/modification-mineure.model";
import {TypeModificationEnum} from "../../../../../../../data/intervenant/models/enums/type-modification.enum";
import {switchMap} from "rxjs/operators";

@Component({
    selector: 'app-edit-informations-mineures',
    templateUrl: './edit-informations-mineures.component.html',
    styleUrls: ['./edit-informations-mineures.component.scss'],
    standalone: false
})
export class EditInformationsMineuresComponent implements OnInit {
    @Input() idOperateur: number;
    @Input() informationsMineures: any;
    @Input() operateur: Operateur;
    @Input() infosDomaine: any[];
    @ViewChild('form', { static: false }) form: NgForm;
    formInformationsMineures: UntypedFormGroup;
    refPays: Pays[];

    get email() {
        return this.formInformationsMineures.get('email');
    }

    /**
     * Initial form values
     */
    initialFormValue: InformationMajeure;

    /**
     * Getter Id intervenant
     */
    get idIntervenant(): number {
      return SessionContext.get('idIntervenant');
    }

    /**
     * Getter id organisme
     */
    get idOrganisme(): number {
      return SessionContext.get('idOrganisme');
    }

    constructor(private readonly fb: UntypedFormBuilder,
                private readonly activeModal: NgbActiveModal,
                private readonly modal: NgbModal,
                private operateurService: OperateursService) {}

    ngOnInit() {
        this.loadPays();

        if (this.operateur) {
            this.formInformationsMineures = this.fb.group({
                nom: [this.operateur.nom || ''],
                prenom: [this.operateur.prenom || ''],
                adresse: [this.operateur!.adresse || ''],
                codePostal: [this.operateur!.codePostal || ''],
                ville: [this.operateur!.ville || ''],
                pays: [this.operateur!.pays ? this.operateur!.pays.id : null],
                telFixe: [this.operateur!.telephoneFixe || ''],
                telMobile: [this.operateur!.telephoneMobile || ''],
                fax: [this.operateur!.fax || ''],
                cedex: [this.operateur!.cedex || ''],
                email: [this.operateur!.email || '', [Validators.required, this.singleEmailValidator]]
            });
        } else {
            this.formInformationsMineures = this.fb.group({
                nom: [''],
                prenom: [''],
                adresse: [''],
                codePostal: [''],
                ville: [''],
                pays: [null],
                telFixe: [''],
                telMobile: [''],
                fax: [''],
                cedex: [''],
                email: ['', [Validators.required, this.singleEmailValidator]]
            });
        }
        // Formater les numéros de téléphones et de fax
        this.formatPhoneNumber('telFixe');
        this.formatPhoneNumber('telMobile');
        this.formatPhoneNumber('fax');

        // Initialise le formulaire
        this.initialFormValue = this.formInformationsMineures.getRawValue();
        this.formInformationsMineures.markAsPristine();
    }

    // Récupère la liste de tous les pays (même s'il n'y en a qu'un dans la BDD..)
    loadPays() {
        this.operateurService.getPays().subscribe(
            (pays: Pays[]) => {
                this.refPays = pays;
            },
            error => {
                console.error('Erreur détaillée lors du chargement des pays:', error);
            }
        );
    }

    onClose() {
        this.activeModal.dismiss();
    }
    // Formate le numéro de téléphone pour qu'il soit plus lisible
    formatPhoneNumber(controlName: string) {
        const control = this.formInformationsMineures.get(controlName);
        if (control) {
            const value = control.value;
            const formattedValue = this.formatNumber(value);
            control.setValue(formattedValue, { emitEvent: false });
        }
    }

    // Méthode pour formater les numéros de téléphones et de fax pour plus de lisibilité (XX XX XX XX XX)
    private formatNumber(value: string): string {
        if (!value) {
            return value;
        }
        const phoneNumber = value.replace(/[^\d]/g, '');
        const phoneNumberLength = phoneNumber.length;
        if (phoneNumberLength < 2) {
            return phoneNumber;
        }
        if (phoneNumberLength < 4) {
            return `${phoneNumber.slice(0, 2)} ${phoneNumber.slice(2)}`;
        }
        if (phoneNumberLength < 6) {
            return `${phoneNumber.slice(0, 2)} ${phoneNumber.slice(2, 4)} ${phoneNumber.slice(4)}`;
        }
        // eslint-disable-next-line max-len
        if (phoneNumberLength < 8) {
            return `${phoneNumber.slice(0, 2)} ${phoneNumber.slice(2, 4)} ${phoneNumber.slice(4, 6)} ${phoneNumber.slice(6)}`;
        }
        // eslint-disable-next-line max-len
        return `${phoneNumber.slice(0, 2)} ${phoneNumber.slice(2, 4)} ${phoneNumber.slice(4, 6)} ${phoneNumber.slice(6, 8)} ${phoneNumber.slice(8, 10)}`;
    }

    // Fonction de validation pour une seule adresse email
    singleEmailValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;
        if (!value) {
            return null;
        }

        // Vérifiez s'il y a plusieurs adresses e-mail en utilisant une regex simple pour détecter les séparateurs courants
        const emails = value.split(/[\s,;]+/);
        if (emails.length > 1) {
            return { multipleEmails: true };
        }

        // Vérifiez si l'adresse e-mail est valide
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
            return { invalidEmail: true };
        }

        return null;
    }

    // Permet de vérifier à la volée si l'email est correct et s'il n'y en a qu'un
    onEmailBlur() {
        this.email.markAsTouched();
    }
    onEmailInput() {
        this.email.updateValueAndValidity();
    }
    // Formate le numéro de téléphone avant de le soumettre pour qu'il n'y ait pas d'espace
    private formatPhoneNumberBeforeSubmit(phoneNumber: string): string {
        return phoneNumber.replace(/\D/g, '');
    }

    onSubmit() {
      const currentFormValue = this.formInformationsMineures.getRawValue();
      const hasDiff = this.hasFormChanged(this.initialFormValue, currentFormValue);

      const toUpdatedPayload = (value: any) => ({
        id: this.idOperateur,
        nom: value.nom,
        prenom: value.prenom,
        email: value.email,
        adresse: value.adresse,
        codePostal: value.codePostal,
        ville: value.ville,
        idPays: value.pays,
        cedex: value.cedex,
        telephoneFixe: this.formatPhoneNumberBeforeSubmit(value.telFixe),
        telephoneMobile: this.formatPhoneNumberBeforeSubmit(value.telMobile),
        fax: this.formatPhoneNumberBeforeSubmit(value.fax)
      });

      const updatedData = toUpdatedPayload(currentFormValue);

      // Si aucune modification : on met à jour directement sans commentaire/modification mineure
      if (!hasDiff) {
        this.operateurService.updateInformationsMineures(this.idOperateur, updatedData).subscribe(
          () => this.activeModal.close('informations_mineures_updated'),
          error => console.error('Erreur lors de la mise à jour des informations mineures', error)
        );
        return;
      }

      // Modifications détectées : ouvrir la modale de commentaire puis ajouter la modification majeure et faire la mise à jour
      const modalRef = this.modal.open(CommentaireModificationComponent, { backdrop: 'static', size: 'lg' });
      modalRef.result.then((result) => {
        // Construction de l'objet ModificationMineure
        const oldPayload = toUpdatedPayload(this.initialFormValue);
        const modificationMineure: ModificationMineure = {
          idOperateur: this.idOperateur,
          dateModification: new Date(),
          idAuteur: this.idIntervenant,
          idOrganisme: this.idOrganisme,
          idModificationType: TypeModificationEnum.MINEURE,
          oldRow: oldPayload,
          newRow: updatedData,
          commentaire: result && result.commentaire ? result.commentaire : ''
        };
        console.log(updatedData);

        this.operateurService.updateInformationsMineures(this.idOperateur, updatedData).pipe(
          switchMap(() => this.operateurService.addModification(modificationMineure))
        ).subscribe(
          () => this.activeModal.close('informations_mineures_updated'),
          error => console.error('Erreur lors de la mise à jour des informations mineures', error)
        );
      }, () => {
        // Dismissed: ne rien faire
      });
    }

    private hasFormChanged(initial: any, current: any): boolean {
      // Comparaison profonde simple basée sur JSON
      try {
        return JSON.stringify(initial) !== JSON.stringify(current);
      } catch {
        return true;
      }
    }
}
